import { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';

// Kawalan Module Global (Superadmin boleh ON/OFF module)
// Bila OFF: menu & akses disembunyikan untuk semua user kecuali superadmin

const PATH_MODULE_MAP = {
  '/classes': 'classes',
  '/activities': 'activities',
  '/daily-logs': 'daily_logs',
  '/nutrition': 'nutrition',
  '/children': 'children',
  '/attendance': 'attendance',
  '/child-registration': 'child_registration',
  '/pendaftaran-kanak-kanak': 'child_registration',
  '/development': 'development',
  '/nutritional-status': 'nutritional_status',
  '/class-transition': 'class_transition',
  '/billing': 'invoices',
  '/reports': 'reports',
  '/album': 'albums',
  '/album-manage': 'albums',
  '/takwim': 'takwim',
  '/manage-teachers': 'manage_teachers',
  '/manage-parents': 'manage_parents',
  '/pending-approval': 'pending_approval',
  '/audit-logs': 'audit_logs',
  '/panduan-ibu-bapa': 'guide',
  '/panduan-admin': 'guide',
  '/panduan': 'guide',
};

export const getModuleForPath = (path) => PATH_MODULE_MAP[path] || null;

// Kunci kumpulan "Tetapan Borang" - berkuatkuasa SEMUA role termasuk superadmin
const FORM_SETTING_KEYS = ['log_time_24h', 'activity_subject', 'nutrition_reference_menu'];

// Cache global + subscriber supaya semua komponen kongsi satu fetch
let cachedModules = null;
let fetchPromise = null;
const subscribers = new Set();

const notifySubscribers = () => {
  subscribers.forEach((fn) => fn(cachedModules));
};

const fetchModules = async () => {
  if (fetchPromise) return fetchPromise;
  fetchPromise = axiosInstance
    .get('/module-access')
    .then((res) => {
      cachedModules = res.data?.modules || {};
      notifySubscribers();
      return cachedModules;
    })
    .catch(() => {
      // Gagal fetch: anggap semua ON (jangan sekat UI tanpa maklumat)
      cachedModules = {};
      notifySubscribers();
      return cachedModules;
    })
    .finally(() => {
      fetchPromise = null;
    });
  return fetchPromise;
};

export const invalidateModuleAccessCache = () => {
  cachedModules = null;
  fetchModules();
};

export const useModuleAccess = (userRole) => {
  const [modules, setModules] = useState(cachedModules);
  const [loading, setLoading] = useState(cachedModules === null && userRole !== 'superadmin');

  useEffect(() => {
    if (!userRole) {
      setLoading(false);
      return;
    }
    // Superadmin: modul akses sentiasa ON, tetapi masih fetch untuk
    // dapatkan nilai sebenar Tetapan Borang (berkuatkuasa semua role)
    if (userRole === 'superadmin') {
      setLoading(false);
    }

    const onUpdate = (mods) => {
      setModules(mods);
      setLoading(false);
    };
    subscribers.add(onUpdate);

    if (cachedModules === null) {
      fetchModules();
    } else {
      setModules(cachedModules);
      setLoading(false);
    }

    return () => subscribers.delete(onUpdate);
  }, [userRole]);

  const isModuleOn = useCallback(
    (moduleKey) => {
      if (!moduleKey) return true;
      // Superadmin bypass HANYA untuk modul akses, bukan Tetapan Borang
      if (userRole === 'superadmin' && !FORM_SETTING_KEYS.includes(moduleKey)) return true;
      if (!modules) return true; // belum dimuatkan - jangan sekat
      return modules[moduleKey] !== false;
    },
    [modules, userRole]
  );

  const isPathAllowed = useCallback(
    (path) => isModuleOn(getModuleForPath(path)),
    [isModuleOn]
  );

  return { modules, loading, isModuleOn, isPathAllowed };
};

// ============ PUBLIC MODULE STATUS (tanpa login, berkuatkuasa semua user) ============
// Untuk module seperti biometric_login, invoiless, subscription_package
let publicModulesCache = null;
let publicFetchPromise = null;

const fetchPublicModules = async () => {
  if (publicModulesCache) return publicModulesCache;
  if (publicFetchPromise) return publicFetchPromise;
  publicFetchPromise = axiosInstance
    .get('/public/module-status')
    .then((res) => {
      publicModulesCache = res.data?.modules || {};
      return publicModulesCache;
    })
    .catch(() => {
      // Gagal fetch: anggap semua ON (jangan sekat tanpa maklumat)
      return {};
    })
    .finally(() => {
      publicFetchPromise = null;
    });
  return publicFetchPromise;
};

export const invalidatePublicModulesCache = () => {
  publicModulesCache = null;
};

export const usePublicModules = () => {
  const [publicModules, setPublicModules] = useState(publicModulesCache);
  const [loading, setLoading] = useState(publicModulesCache === null);

  useEffect(() => {
    let mounted = true;
    fetchPublicModules().then((mods) => {
      if (mounted) {
        setPublicModules(mods);
        setLoading(false);
      }
    });
    return () => { mounted = false; };
  }, []);

  const isPublicModuleOn = useCallback(
    (moduleKey) => {
      if (!moduleKey) return true;
      if (!publicModules) return true; // belum dimuatkan - jangan sekat
      return publicModules[moduleKey] !== false;
    },
    [publicModules]
  );

  return { publicModules, loading, isPublicModuleOn };
};

export default useModuleAccess;
